import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AIAssistantLauncher from './AIAssistantLauncher';
import CollapsedBar from './CollapsedBar';
import ChatPopover from './ChatPopover';
import useSpeechRecognition from './utils/useSpeechRecognition';
import createChatMessage from './utils/createChatMessage';
import {
  useGetActiveConversationMutation,
  useCreateConversationMutation,
  useAddMessageToConversationMutation,
  useCreateSupportTicketMutation,
  useSaveMessageFeedbackMutation,
  useSupportTicketExistsMutation,
  useEndChatMutation
} from '../../features/authentication/chatbotApiSlice';
import './chatbot.scss';

const STREAMING_DELAY_MS = 10;
const MAX_STREAMING_WAIT_MS = 800;
const CHATBOT_STATE_KEY = 'castle-chatbot-shared-state';
const MAX_MESSAGE_LENGTH = 5000;
const OPEN_CHAT_EVENT = 'castle:open-ai-assistant';
const PENDING_OPEN_CHAT_KEY = 'castle:pending-open-ai-assistant';

const getStorageAvailability = () => {
  try {
    const testKey = '__castle_chatbot_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

const getErrorStatusCode = (errorMessage) => {
  const match = /status\s(\d+)/i.exec(errorMessage || '');
  return match ? Number(match[1]) : null;
};

const parseSharedChatbotState = (rawState) => {
  if (!rawState) {
    return null;
  }

  try {
    return JSON.parse(rawState);
  } catch (error) {
    return null;
  }
};

const sleep = (timeMs) => new Promise((resolve) => {
  window.setTimeout(resolve, timeMs);
});

const getStreamingWaitTime = (text, delay = STREAMING_DELAY_MS) => {
  const safeLength = Math.max((text || '').length, 1);
  return Math.min(safeLength * delay, MAX_STREAMING_WAIT_MS);
};

const getAssistantFailureMessage = (errorMessage) => {
  const statusCode = getErrorStatusCode(errorMessage);

  if (statusCode && statusCode >= 500) {
    return 'Chat service unavailable. Please try again later.';
  }

  return 'Failed to send message. Retry?';
};

const getChunkText = (chunkPayload) => {
  if (typeof chunkPayload === 'string') {
    return chunkPayload;
  }

  if (!chunkPayload || typeof chunkPayload !== 'object') {
    return '';
  }

  return chunkPayload.aiReply
    || chunkPayload.chunk
    || chunkPayload.content
    || chunkPayload.text
    || chunkPayload.message
    || '';
};

const extractReplyFromConversationResponse = (responsePayload) => {
  if (!responsePayload) {
    return {
      success: false,
      chunks: [],
      reply: '',
      errorMessage: 'Invalid conversation response.',
      backendMessageId: null
    };
  }

  let chunks = [];

  if (typeof responsePayload === 'string') {
    chunks = [responsePayload];
  } else if (Array.isArray(responsePayload)) {
    chunks = responsePayload.map(getChunkText).filter((chunkText) => chunkText?.length > 0);
  } else {
    const chunkCollections = [
      responsePayload.chunks,
      responsePayload.aiReplyChunks,
      responsePayload.responses,
      responsePayload.messages,
      responsePayload.parts
    ];

    const chunkCollection = chunkCollections.find((candidate) => Array.isArray(candidate));
    if (chunkCollection) {
      chunks = chunkCollection.map(getChunkText).filter((chunkText) => chunkText?.length > 0);
    } else {
      const singleReply = getChunkText(responsePayload);
      if (singleReply) {
        chunks = [singleReply];
      }
    }
  }

  const reply = chunks.join('');
  const backendMessageId = responsePayload?.id || responsePayload?.messageId || responsePayload?.message?.id || null;

  if (!reply?.trim()) {
    return {
      success: false,
      chunks: [],
      reply: '',
      errorMessage: responsePayload?.errorMessage || 'Received empty response from assistant',
      backendMessageId: null
    };
  }

  return {
    success: true,
    chunks,
    reply,
    errorMessage: '',
    backendMessageId
  };
};

const getRequestErrorDetails = (requestError) => {
  const status = requestError?.status || requestError?.originalStatus;
  if (status) {
    const apiErrorMessage = requestError?.data?.errorMessage
      || requestError?.data?.error
      || requestError?.data?.message;

    return {
      statusCode: Number(status),
      detail: apiErrorMessage || `Request failed with status ${status}`
    };
  }

  const fallbackMessage = requestError?.message || 'Failed to send message. Retry?';
  return {
    statusCode: getErrorStatusCode(fallbackMessage),
    detail: fallbackMessage
  };
};

const getConversationIdFromResponse = (responsePayload) => {
  if (responsePayload == null) {
    return null;
  }

  return responsePayload.id;
};

const getMessageText = (message) => message?.content || '';

const normalizeActiveConversationResponse = (activeConversationResponse) => {
  if (!activeConversationResponse) {
    return {
      hasConversation: false,
      conversationId: null,
      messages: []
    };
  }

  const conversationPayload = activeConversationResponse;

  const conversationId = getConversationIdFromResponse(conversationPayload);
  const messages = Array.isArray(conversationPayload?.messages) ? conversationPayload.messages : [];

  return {
    hasConversation: conversationId !== null && conversationId !== undefined && conversationId !== '',
    conversationId,
    messages
  };
};

const mapConversationMessages = (messagesResponse) => {
  if (!Array.isArray(messagesResponse) || messagesResponse.length === 0) {
    return [];
  }

  return messagesResponse.map((message) => createChatMessage(
    String(message.id),
    message.sender === 1 ? 'bot' : 'user',
    getMessageText(message),
    'complete',
    {
      ...(message.rating != null
        ? {
          feedback: message.rating === 0 ? 'up' : 'down',
          feedbackSubmitted: true
        }
        : {})
    }
  ));
};

const createInitialMessages = (botName) => [
  createChatMessage('m-0', 'bot', `Hi! I'm ${botName}. How can I help you today?`, 'complete')
];

const ChatbotContainer = ({
  botName,
  botTitle
}) => {
  const [getActiveConversation] = useGetActiveConversationMutation();
  const [createConversation] = useCreateConversationMutation();
  const [addMessageToConversation] = useAddMessageToConversationMutation();
  const [createSupportTicket] = useCreateSupportTicketMutation();
  const [saveMessageFeedback] = useSaveMessageFeedbackMutation();
  const [checkTicketExists] = useSupportTicketExistsMutation();
  const [endChat] = useEndChatMutation();

  const messageIdRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState(() => createInitialMessages(botName));
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActiveConversationLoading, setIsActiveConversationLoading] = useState(false);
  const [ticketSubmission, setTicketSubmission] = useState({ state: 'idle', message: '' });
  const [feedbackModal, setFeedbackModal] = useState({ open: false, messageId: '', errorMessage: '',});
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [ticketConfirmationModalOpen, setTicketConfirmationModalOpen] = useState(false);
  const [hasLocalStorage] = useState(() => getStorageAvailability());
  const messagesRef = useRef(messages);
  const messageListRef = useRef(null);
  const popoverWrapperRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  const {
    isSupported,
    isListening,
    transcript,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript
  } = useSpeechRecognition();

  const browserSupportMessage = hasLocalStorage
    ? ''
    : 'Chat history sync is unavailable in this browser.';

  const setMessagesWithRef = (updater) => {
    setMessages((previousMessages) => {
      const nextMessages = updater(previousMessages);
      messagesRef.current = nextMessages;
      return nextMessages;
    });
  };

  const getNextMessageId = () => {
    messageIdRef.current += 1;
    return `m-${messageIdRef.current}`;
  };

  const updateMessageById = (messageId, updater) => {
    setMessagesWithRef((currentMessages) => currentMessages.map((message) => {
      if (message.id !== messageId) {
        return message;
      }

      return {
        ...message,
        ...updater(message)
      };
    }));
  };

  const findMessageById = (messageId) => messagesRef.current.find((message) => message.id === messageId) || null;

  const fetchActiveConversation = async ({
    hydrateMessages = false,
    errorMessage = 'Could not fetch active conversation:'
  } = {}) => {
    try {
      const activeConversationResponse = await getActiveConversation().unwrap();
      const normalizedConversation = normalizeActiveConversationResponse(activeConversationResponse);

      if (!normalizedConversation.hasConversation || !normalizedConversation.conversationId) {
        return null;
      }

      setConversationId(normalizedConversation.conversationId);

      if (hydrateMessages) {
        const loadedMessages = mapConversationMessages(
          normalizedConversation.messages
        );

        if (loadedMessages.length > 0) {
          setMessagesWithRef(() => loadedMessages);
        }
      }

      return normalizedConversation;
    } catch (error) {
      console.warn(errorMessage, error);
      return null;
    }
  };

  useEffect(() => {
    if (!hasLocalStorage) {
      return undefined;
    }

    const syncSharedChatState = (rawState) => {
      const parsedState = parseSharedChatbotState(rawState);

      if (parsedState?.isOpen === true || parsedState?.isOpen === false) {
        setIsOpen(parsedState.isOpen);
        return;
      }

      if (rawState) {
        window.localStorage.removeItem(CHATBOT_STATE_KEY);
      }
    };

    syncSharedChatState(window.localStorage.getItem(CHATBOT_STATE_KEY));

    const handleStorage = (event) => {
      if (event.key !== CHATBOT_STATE_KEY || !event.newValue) {
        return;
      }

      syncSharedChatState(event.newValue);
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [hasLocalStorage]);

  useEffect(() => {
    if (!hasLocalStorage) {
      return;
    }

    window.localStorage.setItem(
      CHATBOT_STATE_KEY,
      JSON.stringify({
        isOpen
      })
    );
  }, [hasLocalStorage, isOpen]);

  useEffect(() => {

    isOpenRef.current = isOpen;
    if (isOpen) {
      setUnreadCount(0);
    }

    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (ticketConfirmationModalOpen || feedbackModal.open) {
        return;
      }

      if (popoverWrapperRef.current && !popoverWrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, ticketConfirmationModalOpen, feedbackModal.open]);

  useEffect(() => {
    if (transcript) {
      setInputValue(transcript);
    }
  }, [transcript]);

  useEffect(() => {
    if (speechError) {
      const noticeId = getNextMessageId();
      setMessagesWithRef((currentMessages) => [
        ...currentMessages,
        createChatMessage(noticeId, 'bot', `Voice input error: ${speechError}`, 'error', {
          errorMessage: `Voice input error: ${speechError}`
        })
      ]);
    }
  }, [speechError]);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
    };

    if (window.sessionStorage.getItem(PENDING_OPEN_CHAT_KEY) === '1') {
      setIsOpen(true);
      window.sessionStorage.removeItem(PENDING_OPEN_CHAT_KEY);
    }

    window.addEventListener(OPEN_CHAT_EVENT, handleOpenChat);
    return () => {
      window.removeEventListener(OPEN_CHAT_EVENT, handleOpenChat);
    };
  }, []);

  useEffect(() => {
    const checkAndInitializeConversation = async () => {
      if (!isOpen || conversationId) {
        return;
      }

      setIsActiveConversationLoading(true);
      try {
        await fetchActiveConversation({
          hydrateMessages: true,
          errorMessage: 'Could not check for active conversation:'
        });
      } finally {
        setIsActiveConversationLoading(false);
      }
    };

    checkAndInitializeConversation();
  }, [isOpen, conversationId]);

  const createNewConversation = async () => {
    try {
      const response = await createConversation().unwrap();
      if (response && response.conversationId) {
        setConversationId(response.conversationId);
        return response.conversationId;
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
    return null;
  };

  const saveMessageToConversation = async (conversationIdToUse, message) => {
    if (!conversationIdToUse) {
      return null;
    }

    const options = {
      conversationId: conversationIdToUse,
      role: message.role === 'user' ? '0' : '1',
      message: message.text
    };

    try {
      const response = await addMessageToConversation(options).unwrap();
      if (!response || !response.aiReply) {
        console.warn('Bot message not found in conversation response. Response:', response);
      }
      return response;
    } catch (error) {
      console.error('Failed to save message to conversation:', error);
      return null;
    }
  };

  const fetchBotResponse = async (prompt, targetMessageId, prefetchedResponse = null) => {
    setIsLoading(true);
    let partialText = '';

    try {
      if (!prefetchedResponse) {
        throw new Error('Bot response should be included in user message save response.');
      }

      const parsedResponse = extractReplyFromConversationResponse(prefetchedResponse);

      if (!parsedResponse.success) {
        throw new Error(parsedResponse.errorMessage);
      }


      const normalizedChunks = parsedResponse.chunks.length > 0
        ? parsedResponse.chunks
        : [parsedResponse.reply];

      for (const chunkText of normalizedChunks) {
        partialText += chunkText;

        updateMessageById(targetMessageId, () => ({
          text: partialText,
          status: 'loading',
          retryPrompt: prompt,
          streamingDelay: STREAMING_DELAY_MS
        }));

        await sleep(getStreamingWaitTime(chunkText, STREAMING_DELAY_MS));
      }

      if (!isOpenRef.current) {
        setUnreadCount((prev) => prev + 1);
      }

      updateMessageById(targetMessageId, () => ({
        ...(parsedResponse.backendMessageId ? { id: String(parsedResponse.backendMessageId) } : {}),
        text: partialText.trim(),
        status: 'complete',
        retryPrompt: prompt,
        streamingDelay: STREAMING_DELAY_MS,
        errorMessage: ''
      }));


    } catch (requestError) {
      const { detail } = getRequestErrorDetails(requestError);
      const nextErrorMessage = getAssistantFailureMessage(detail);

      updateMessageById(targetMessageId, () => ({
        text: partialText,
        status: 'error',
        retryPrompt: prompt,
        errorDetail: detail,
        errorMessage: nextErrorMessage,
        streamingDelay: STREAMING_DELAY_MS
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const sendPrompt = async (prompt) => {
    const cleanPrompt = prompt.trim();
    if (!cleanPrompt || isLoading) {
      return;
    }

    if (ticketSubmission.state !== 'idle') {
      setTicketSubmission({ state: 'idle', message: '' });
    }

    let currentConversationId = conversationId;
    if (!currentConversationId) {
      const resolvedConversation = await fetchActiveConversation({
        errorMessage: 'Could not get active conversation before sending message:'
      });

      if (resolvedConversation?.conversationId) {
        currentConversationId = resolvedConversation.conversationId;
      }
    }

    if (!currentConversationId) {
      currentConversationId = await createNewConversation();
      if (!currentConversationId) {
        console.warn('Could not create conversation, proceeding without storage');
      }
    }

    const isTooLong = cleanPrompt.length > MAX_MESSAGE_LENGTH;
    const nextPrompt = isTooLong ? cleanPrompt.slice(0, MAX_MESSAGE_LENGTH) : cleanPrompt;
    const userMessage = createChatMessage(getNextMessageId(), 'user', nextPrompt, 'complete');
    const botMessageId = getNextMessageId();
    const botMessage = createChatMessage(botMessageId, 'bot', '', 'loading', {
      retryPrompt: nextPrompt,
      streamingDelay: STREAMING_DELAY_MS
    });

    setInputValue('');

    setMessagesWithRef((currentMessages) => {
      if (!isTooLong) {
        return [...currentMessages, userMessage, botMessage];
      }

      const warningMessage = createChatMessage(
        getNextMessageId(),
        'bot',
        'Your message exceeded 5000 characters and was truncated before sending.',
        'complete'
      );
      return [...currentMessages, userMessage, warningMessage, botMessage];
    });

    const savedMessageResponse = await saveMessageToConversation(currentConversationId, userMessage);

    resetTranscript();
    await fetchBotResponse(nextPrompt, botMessageId, savedMessageResponse);
  };

  const handleSend = () => {
    sendPrompt(inputValue);
  };

  const resetChatToInitialState = () => {
    messageIdRef.current = 0;
    const initialMessages = createInitialMessages(botName);

    setMessagesWithRef(() => initialMessages);
    setInputValue('');
    setConversationId(null);
    setUnreadCount(0);
    setIsSubmittingFeedback(false);
    setTicketConfirmationModalOpen(false);
    setFeedbackModal({ open: false, messageId: '', errorMessage: ''});
    setTicketSubmission({ state: 'idle', message: '' });

    if (isListening) {
      stopListening();
    }
    resetTranscript();
  };

  const handleEndChat = async () => {
    if (isLoading || ticketSubmission.state === 'ending') {
      return;
    }

    setTicketSubmission({ state: 'ending', message: '' });

    try {
      if (conversationId) {
        await endChat({ conversationId }).unwrap();
      }
    } catch (error) {
      console.warn('Could not end chat on server. Resetting local chat state.', error);
    } finally {
      resetChatToInitialState();
      setIsOpen(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (isLoading || ticketSubmission.state === 'submitting' || conversationId == null) {
      return;
    }

    try {
      const ticketExistsResponse = await checkTicketExists({ conversationId }).unwrap();

      if (ticketExistsResponse) {
        setTicketConfirmationModalOpen(true);
        return;
      }

      await submitSupportTicket();
    } catch (error) {
      console.warn('Could not check if ticket exists:', error);
      await submitSupportTicket();
    }
  };

  const submitSupportTicket = async () => {

    try {
      setTicketSubmission({ state: 'submitting', message: '' });
      await createSupportTicket({ conversationId }).unwrap();
      setTicketSubmission({
        state: 'success',
        message: 'We captured this conversation and sent it to our support team.'
      });
      setTicketConfirmationModalOpen(false);
    } catch (submitError) {
      setTicketSubmission({
        state: 'error',
        message: 'Could not submit ticket. Retry?'
      });
    }
  };

  const handleConfirmTicketSubmission = async () => {
    setTicketConfirmationModalOpen(false);
    await submitSupportTicket();
  };

  const handleCancelTicketSubmission = () => {
    setTicketConfirmationModalOpen(false);
  };

  const handleRetry = async (messageId) => {
    if (isLoading) {
      return;
    }

    const targetMessage = findMessageById(messageId);
    if (!targetMessage) {
      return;
    }

    if (!targetMessage.retryPrompt) {
      return;
    }

    updateMessageById(messageId, () => ({
      status: 'loading',
      text: '',
      feedback: null,
      feedbackSubmitted: false,
      errorMessage: '',
      streamingDelay: STREAMING_DELAY_MS
    }));
    const retryUserMessage = {
      role: 'user',
      text: targetMessage.retryPrompt
    };

    const savedMessageResponse = await saveMessageToConversation(conversationId, retryUserMessage);
    await fetchBotResponse(targetMessage.retryPrompt, messageId, savedMessageResponse);
  };

  const handleFeedback = async (messageId, rating) => {
    const targetMessage = findMessageById(messageId);

    if (targetMessage?.feedbackSubmitted && targetMessage.feedback === rating) {
      return;
    }

    if (!targetMessage || !targetMessage.id) {
      return;
    }

    if (rating === 'down') {
      updateMessageById(messageId, () => ({ feedback: 'down' }));
      setFeedbackModal({ open: true, messageId, errorMessage: ''});
      return;
    }

    const submitPositiveFeedback = async () => {
      try {
        await saveMessageFeedback({
          messageId: targetMessage.id,
          rating: 0
        }).unwrap();

        updateMessageById(messageId, () => ({
          feedback: 'up',
          feedbackSubmitted: true
        }));
      } catch (submitError) {
        updateMessageById(messageId, () => ({
          feedback: null,
          feedbackSubmitted: false
        }));
      }
    };

    submitPositiveFeedback();
  };

  const handleFeedbackCancel = async () => {
    if (!feedbackModal.messageId) {
      return;
    }

    await saveMessageFeedback({
      messageId: feedbackModal.messageId,
      rating: 1,
      explanation: null
    }).unwrap();
    
    if (feedbackModal.messageId) {
      updateMessageById(feedbackModal.messageId, () => ({
        feedback: 'down',
        feedbackSubmitted: true
      }));
    }

    setFeedbackModal({ open: false, messageId: '', errorMessage: ''});
  };

  const handleFeedbackSubmit = async (detailText) => {
    if (!feedbackModal.messageId) {
      setFeedbackModal((currentState) => ({
        ...currentState,
        errorMessage: 'Message id not available for feedback.'
      }));
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      await saveMessageFeedback({
        messageId: feedbackModal.messageId,
        rating: 1,
        explanation: detailText
      }).unwrap();

      updateMessageById(feedbackModal.messageId, () => ({
        feedback: 'down',
        feedbackDetail: detailText,
        feedbackSubmitted: true
      }));
      setFeedbackModal({ open: false, messageId: '', errorMessage: ''});
    } catch (submitError) {
      setFeedbackModal((currentState) => ({
        ...currentState,
        errorMessage: 'Could not save feedback. Retry.'
      }));
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleMicPress = () => {
    if (!isSupported) {
      return;
    }

    if (isListening) {
      stopListening();
      return;
    }

    startListening();
  };

  return (
    <div className={`chatbot-root ${isOpen ? 'is-open' : 'is-closed'}`}>
      <ChatPopover
        isOpen={isOpen}
        botName={botName}
        botTitle={botTitle}
        messages={messages}
        inputValue={inputValue}
        isLoading={isLoading}
        isActiveConversationLoading={isActiveConversationLoading}
        isRecording={isListening}
        isVoiceSupported={isSupported}
        browserSupportMessage={browserSupportMessage}
        ticketSubmissionState={ticketSubmission.state}
        ticketSubmissionMessage={ticketSubmission.message}
        feedbackModalOpen={feedbackModal.open}
        feedbackMessageId={feedbackModal.messageId}
        feedbackErrorMessage={feedbackModal.errorMessage}
        isSubmittingFeedback={isSubmittingFeedback}
        ticketConfirmationModalOpen={ticketConfirmationModalOpen}
        onClose={() => setIsOpen(false)}
        onInputChange={setInputValue}
        onSend={handleSend}
        onMicPress={handleMicPress}
        onRetry={handleRetry}
        onFeedback={handleFeedback}
        onFeedbackCancel={handleFeedbackCancel}
        onFeedbackSubmit={handleFeedbackSubmit}
        onSubmitTicket={handleSubmitTicket}
        onEndChat={handleEndChat}
        onConfirmTicketSubmission={handleConfirmTicketSubmission}
        onCancelTicketSubmission={handleCancelTicketSubmission}
        messageListRef={messageListRef}
        popoverRef={popoverWrapperRef}
      />
      {isOpen ? (
        <CollapsedBar
          isOpen={isOpen}
          onToggle={() => setIsOpen(false)}
          label="AI Chat"
        />
      ) : (
        <AIAssistantLauncher
          isOpen={isOpen}
          onToggle={() => setIsOpen(true)}
          unreadCount={unreadCount}
        />
      )}
    </div>
  );
};

ChatbotContainer.propTypes = {
  botName: PropTypes.string,
  botTitle: PropTypes.string
};

ChatbotContainer.defaultProps = {
  botName: 'Cassie',
  botTitle: 'Support Assistant'
};

export default ChatbotContainer;
